/**
 * Bundle image loader — loads featured product images for Simple Bundles blocks.
 *
 * Scans [data-bundle-handles] elements from bundle-images.liquid, fetches each
 * unique handle once per page via /products/{handle}.json, and injects images
 * into matching scroller cells. Respects data-bundle-links on the root element:
 * when true, dynamically created cells are individual product links; otherwise
 * they are divs and the root element carries the product link.
 *
 * Pre-rendered cells (e.g. with data-bundle-variant-id on the PDP) are reused
 * instead of creating duplicates.
 *
 * Re-scan after dynamic content:
 *   document.dispatchEvent(new CustomEvent('bundle-images:rescan', { detail: { root } }))
 */

const RESCAN_EVENT = 'bundle-images:rescan';

const imageCache = new Map();
const inflight = new Map();

class BundleImageLoader {
  /** @type {string} */
  static RESCAN_EVENT = RESCAN_EVENT;

  /**
   * @param {ParentNode} [root=document]
   */
  static async load(root = document) {
    const handleToCells = collectPendingCells(root);
    if (!handleToCells.size) return;

    await populateHandles(handleToCells);
  }

  /**
   * @param {ParentNode} [root=document]
   */
  static rescan(root = document) {
    return BundleImageLoader.load(root);
  }
}

/**
 * @param {ParentNode} root
 * @returns {Map<string, HTMLElement[]>}
 */
function collectPendingCells(root) {
  const handleToCells = new Map();

  for (const bundle of root.querySelectorAll('[data-bundle-handles]')) {
    const rawHandles = bundle.dataset.bundleHandles;
    if (!rawHandles) continue;

    const scroller = bundle.querySelector('.pkg-card__images-scroller');
    if (!scroller) continue;

    const useBundleLinks = usesBundleLinks(bundle);
    const uniqueHandles = [...new Set(rawHandles.split(',').filter(Boolean))];

    for (const handle of uniqueHandles) {
      const existingCells = findCellsForHandle(scroller, handle);
      const variantCells = existingCells.filter((cell) => cell.dataset.bundleVariantId);
      const cellsToUse = [];

      if (variantCells.length > 0) {
        for (const cell of variantCells) {
          if (!isCellResolved(cell)) {
            cellsToUse.push(cell);
          }
        }
      } else {
        let cell = existingCells.find((el) => !el.dataset.bundleVariantId);

        if (!cell) {
          cell = createSkeletonCell(handle, useBundleLinks);
          scroller.appendChild(cell);
        }

        if (!isCellResolved(cell)) {
          cellsToUse.push(cell);
        }
      }

      for (const cell of cellsToUse) {
        if (!handleToCells.has(handle)) {
          handleToCells.set(handle, []);
        }
        handleToCells.get(handle).push(cell);
      }
    }
  }

  return handleToCells;
}

/**
 * @param {Element} scroller
 * @param {string} handle
 * @returns {HTMLElement[]}
 */
function findCellsForHandle(scroller, handle) {
  return [...scroller.querySelectorAll('[data-bundle-handle]')].filter(
    (cell) => cell.dataset.bundleHandle === handle
  );
}

/**
 * @param {HTMLElement} cell
 * @returns {boolean}
 */
function isCellResolved(cell) {
  return Boolean(cell.querySelector('img')) || cell.classList.contains('pkg-card__img-cell--no-image');
}

/**
 * @param {HTMLElement} bundle
 * @returns {boolean}
 */
function usesBundleLinks(bundle) {
  return bundle.dataset.bundleLinks === 'true';
}

/**
 * @param {string} handle
 * @param {boolean} useBundleLinks
 * @returns {HTMLAnchorElement | HTMLDivElement}
 */
function createSkeletonCell(handle, useBundleLinks) {
  const cell = document.createElement(useBundleLinks ? 'a' : 'div');
  cell.className = 'pkg-card__img-cell pkg-card__img-cell--skeleton';
  cell.dataset.bundleHandle = handle;

  if (useBundleLinks) {
    cell.href = `/products/${encodeURIComponent(handle)}`;
  }

  return cell;
}

/**
 * @param {Map<string, HTMLElement[]>} handleToCells
 */
async function populateHandles(handleToCells) {
  const handles = [...handleToCells.keys()];
  const pending = [];

  for (const handle of handles) {
    if (imageCache.has(handle)) {
      const { src, alt } = imageCache.get(handle);
      for (const cell of handleToCells.get(handle) ?? []) {
        injectImage(cell, src, alt);
      }
    } else {
      pending.push(handle);
    }
  }

  if (!pending.length) return;

  const results = await Promise.allSettled(pending.map((h) => fetchProductImage(h)));

  for (const res of results) {
    if (res.status !== 'fulfilled' || !res.value) continue;

    const { handle, src, alt } = res.value;

    for (const cell of handleToCells.get(handle) ?? []) {
      injectImage(cell, src, alt);
    }
  }
}

/**
 * @param {string} handle
 * @returns {Promise<{ handle: string, src: string | null, alt: string }>}
 */
async function fetchProductImage(handle) {
  if (imageCache.has(handle)) return imageCache.get(handle);
  if (inflight.has(handle)) return inflight.get(handle);

  const promise = resolveProductImage(handle);
  inflight.set(handle, promise);

  try {
    const result = await promise;
    imageCache.set(handle, result);
    return result;
  } finally {
    inflight.delete(handle);
  }
}

async function resolveProductImage(handle) {
  let result = await attemptFetch(handle);
  if (result.src) return result;

  const clean = sanitizeHandle(handle);
  if (clean !== handle) {
    const retry = await attemptFetch(clean);
    if (retry.src) {
      return { handle, src: retry.src, alt: retry.alt };
    }
  }

  return result;
}

async function attemptFetch(handle) {
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.json`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return { handle, src: null, alt: '' };

    const { product } = await res.json();
    const img = product?.image;
    return img?.src
      ? { handle, src: img.src, alt: img.alt || '' }
      : { handle, src: null, alt: '' };
  } catch {
    return { handle, src: null, alt: '' };
  }
}

function sanitizeHandle(handle) {
  return handle
    .replace(/[\u00AE\u2122\u00A9]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * @param {HTMLElement} cell
 * @param {string | null} src
 * @param {string} alt
 */
function injectImage(cell, src, alt) {
  if (isCellResolved(cell)) return;

  if (src) {
    const img = document.createElement('img');
    img.src = src.includes('?') ? `${src}&width=300` : `${src}?width=300`;
    img.alt = alt;
    img.loading = 'lazy';
    img.width = 300;
    img.height = 300;
    cell.classList.remove('pkg-card__img-cell--skeleton');
    cell.appendChild(img);
  } else {
    cell.classList.remove('pkg-card__img-cell--skeleton');
    cell.classList.add('pkg-card__img-cell--no-image');
  }
}

function bootstrap() {
  BundleImageLoader.load();
}

document.addEventListener(RESCAN_EVENT, (event) => {
  const root = event.detail?.root ?? document;
  BundleImageLoader.load(root);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}

export { BundleImageLoader, RESCAN_EVENT };
